import { CardData } from './card-data';
import { GroupDetailEnergyScore } from '../enum/dto/group-detail-energy-score';
import { memoize } from '../../modules/shared/decorators/memoize';
import {
  SectionColumnConfigDataForCannabinoidsAndTerpenesColumn,
  SectionColumnConfigDataValue
} from '../enum/shared/section-column-config-data-value';
import { Variant } from '../product/dto/variant';
import { ProductType } from '../enum/dto/product-type.enum';
import { VariantTypeUtils } from '../../utils/variant-type-utils';

export class MissJonesCardData extends CardData {
  /**
   * This card has a color bar that hugs the top of the card. Due to printer and tray feed inaccuracy, we need a bleed
   * area at the top of the card. Therefore, allow the background to overflow the card boundaries so that the color
   * bar can extend into the bleed area to avoid any white lines appearing at the top, top-left, or top-right of the
   * card when printed.
   */
  override allowPrintCardBackgroundInflatorOverflow(): boolean {
    return this.section?.isOnPerforatedPaper();
  }

  @memoize
  override getCannabinoid(
    cannabinoid: string,
    displayFormat: SectionColumnConfigDataValue = SectionColumnConfigDataForCannabinoidsAndTerpenesColumn.NumericValue
  ): string {
    const c = super.getCannabinoid(cannabinoid, displayFormat);
    const empty = c?.includes('<') || c === 'N/A' || c === '-' || c === '--';
    return empty ? null : c;
  }

  @memoize
  public override getTerpene(
    terpenePascalCased: string,
    displayFormat: SectionColumnConfigDataValue = SectionColumnConfigDataForCannabinoidsAndTerpenesColumn.NumericValue
  ): string {
    const t = super.getTerpene(terpenePascalCased, displayFormat);
    const empty = t?.includes('<') || t === 'N/A' || t === '-' || t === '--';
    return empty ? null : t;
  }

  public override getSize(variant?: Variant): string {
    const v = variant || this.rowVariants?.firstOrNull();
    if (v?.packagedQuantity > 1) {
      const formattedUnitSize = v?.getFormattedUnitSize();
      switch (v?.productType) {
        case ProductType.Beverage:
        case ProductType.Concentrate:
        case ProductType.Flower:
        case ProductType.Vape:
        case ProductType.Wellness:
          return `${v?.packagedQuantity} x ${formattedUnitSize}`;
        case ProductType.Oil: {
          if (!VariantTypeUtils.isCapsuleType(v?.variantType)) {
            return `${v?.packagedQuantity} x ${formattedUnitSize}`;
          }
          break;
        }
        case ProductType.Other: {
          if (VariantTypeUtils.isOtherTypeWithCannabinoids(v?.variantType)) {
            return `${v?.packagedQuantity} x ${formattedUnitSize}`;
          }
          break;
        }
      }
    }
    return super.getSize(variant);
  }

  public getHeaderText(): string {
    return this.rowVariantType();
  }

  public getExperienceText(): string {
    const variant = this.rowVariants?.firstOrNull();
    return MissJonesCardData.getExperienceText(variant?.energyScore);
  }

  public hasCraftDesignation(): boolean {
    return this.rowVariants?.some(variant => variant?.hasCraftDesignation());
  }

  private static getExperienceText(energyScore: GroupDetailEnergyScore): string {
    switch (energyScore) {
      case GroupDetailEnergyScore.EnergyScore_1:
      case GroupDetailEnergyScore.EnergyScore_2:
        return 'Heavy';
      case GroupDetailEnergyScore.EnergyScore_3:
      case GroupDetailEnergyScore.EnergyScore_4:
        return 'Chill';
      case GroupDetailEnergyScore.EnergyScore_5:
      case GroupDetailEnergyScore.EnergyScore_6:
        return 'Easygoing';
      case GroupDetailEnergyScore.EnergyScore_7:
      case GroupDetailEnergyScore.EnergyScore_8:
        return 'Uplifting';
      case GroupDetailEnergyScore.EnergyScore_9:
      case GroupDetailEnergyScore.EnergyScore_10:
        return 'Energizing';
      default:
        return null;
    }
  }
}
